import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ExternalLink, ThumbsUp, Globe, Users } from 'lucide-react';
import { getStartupBySlug, getStartups, upvoteStartup, downvoteStartup } from '../services/api';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import { Loader2 } from 'lucide-react';

const StartupDetailPage = () => {
  const { slug } = useParams();
  const [startup, setStartup] = useState(null);
  const [relatedStartups, setRelatedStartups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [upvoted, setUpvoted] = useState(false);
  const [votes, setVotes] = useState(0);
  const [isVoting, setIsVoting] = useState(false);
  const [sortBy, setSortBy] = useState('latest');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const data = await getStartupBySlug(slug);
        setStartup(data);
        setVotes(data.rating);
        
        // Fetch related startups
        const all = await getStartups({ limit: 100 });
        const related = all.filter(s => s.categorySlug === data.categorySlug && s.slug !== slug).slice(0, 3);
        setRelatedStartups(related);
      } catch (error) {
        console.error('Failed to fetch startup:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [slug]);

  const handleUpvote = async () => {
    if (isVoting || !startup) return;
    setIsVoting(true);
    
    try {
      if (upvoted) {
        await downvoteStartup(startup.id);
        setVotes(votes - 1);
      } else {
        await upvoteStartup(startup.id);
        setVotes(votes + 1);
      }
      setUpvoted(!upvoted);
    } catch (error) {
      console.error('Failed to vote:', error);
    } finally {
      setIsVoting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header sortBy={sortBy} setSortBy={setSortBy} />
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
        </div>
      </div>
    );
  }

  if (!startup) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900">Startup not found</h1>
          <Link to="/" className="mt-4 inline-block text-emerald-600 hover:text-emerald-700">
            ← Back to home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header sortBy={sortBy} setSortBy={setSortBy} />
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back button */}
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to startups</span>
        </Link>

        {/* Main content */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {/* Screenshot */}
          <div className="aspect-video bg-gray-100">
            <img
              src={startup.image}
              alt={`${startup.name} screenshot`}
              className="w-full h-full object-cover"
            />
          </div>

          {/* Info */}
          <div className="p-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{startup.name}</h1>
                <p className="mt-2 text-gray-600">{startup.description}</p>
              </div>
              
              <Button
                onClick={handleUpvote}
                disabled={isVoting}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                  upvoted
                    ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                variant="ghost"
              >
                <ThumbsUp className={`w-5 h-5 ${upvoted ? 'fill-emerald-600' : ''}`} />
                <span>{votes}</span>
              </Button>
            </div>

            {/* Meta info */}
            <div className="mt-6 flex flex-wrap gap-4">
              <div className="flex items-center gap-2 text-gray-600">
                <Globe className="w-4 h-4" />
                <span>{startup.country}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <Users className="w-4 h-4" />
                <span>{startup.team}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="px-3 py-1 rounded-full bg-gray-100 text-sm font-medium text-gray-700">
                  {startup.category}
                </span>
              </div>
            </div>

            {/* Visit button */}
            <div className="mt-6">
              <a
                href={`https://${startup.slug.replace(/-/g, '.')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-medium rounded-lg transition-colors"
              >
                <ExternalLink className="w-4 h-4" />
                <span>Visit Website</span>
              </a>
            </div>
          </div>
        </div>

        {/* Related startups */}
        {relatedStartups.length > 0 && (
          <div className="mt-12">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Similar Startups</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {relatedStartups.map(related => (
                <Link
                  key={related.id}
                  to={`/startup/${related.slug}`}
                  className="block p-4 bg-white rounded-lg border border-gray-200 hover:border-gray-300 hover:shadow-md transition-all"
                >
                  <h3 className="font-medium text-gray-900">{related.name}</h3>
                  <p className="mt-1 text-sm text-gray-600 line-clamp-2">{related.description}</p>
                </Link>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default StartupDetailPage;
